import { useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Button,
  Divider,
  Link,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { getPackagingStockLevel } from 'src/utils/packaging-stock-level';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPackagingItemHistory } from 'src/redux/slices/packaging-item';

import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import TableErrorData from 'src/components/table/table-error-data';
import { TableHeadCustom, TableNoData, TableSkeleton } from 'src/components/table';

import { IPackagingItem, IPackagingItemHistoryEntry } from 'src/types/packaging-item';

const LEDGER_HEADERS = [
  { id: 'type', label: 'Type', align: 'left' },
  { id: 'reference', label: 'Reference', align: 'left' },
  { id: 'date', label: 'Date', align: 'left' },
  { id: 'quantity', label: 'Quantity', align: 'left' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'history', label: 'Stock History' },
];

const TYPE_LABEL: Record<IPackagingItemHistoryEntry['type'], string> = {
  purchase: 'Purchase',
  dispatch: 'Dispatch',
  return: 'Return',
  'stock-count': 'Stock Count',
  damage: 'Damage',
  adjustment: 'Adjustment',
};

const TYPE_COLOR: Record<
  IPackagingItemHistoryEntry['type'],
  'success' | 'warning' | 'info' | 'primary' | 'error' | 'default'
> = {
  purchase: 'success',
  dispatch: 'warning',
  return: 'info',
  'stock-count': 'primary',
  damage: 'error',
  adjustment: 'default',
};

const REFERENCE_PATH: Record<IPackagingItemHistoryEntry['type'], (id: string) => string> = {
  purchase: paths.dashboard.packagingPurchaseOrder.view,
  dispatch: paths.dashboard.packagingDispatch.view,
  return: paths.dashboard.packagingReturn.view,
  'stock-count': paths.dashboard.packagingStockCount.view,
  damage: paths.dashboard.packagingDamageDispatch.view,
  // Adjustments have no source record page.
  adjustment: () => '',
};

type Props = {
  currentPackagingItem?: IPackagingItem;
};

export default function PackagingItemDetails({ currentPackagingItem }: Props) {
  const [tabValue, setTabValue] = useState('overview');

  const { user: authUser } = useAuthContext();

  const dispatch = useAppDispatcher();

  const { history } = useAppSelector((state) => state.packagingItem);

  useEffect(() => {
    if (tabValue === 'history' && currentPackagingItem?._id) {
      dispatch(getPackagingItemHistory({ itemID: currentPackagingItem._id }));
    }
  }, [tabValue, currentPackagingItem, dispatch]);

  const stockLevel = getPackagingStockLevel(
    currentPackagingItem?.quantity ?? 0,
    currentPackagingItem?.reorderThreshold ?? 0
  );

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, value) => setTabValue(value)}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {tabValue === 'overview' && (
            <Grid container spacing={2} mt={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle1">Basic Information</Typography>

                {hasPermission(
                  authUser,
                  'any',
                  AuthPermissions.UPDATE_PACKAGING_ITEM,
                  AuthPermissions.ADMIN_PERMISSION,
                  AuthPermissions.SUPER_ADMIN_PERMISSION
                ) && (
                  <Link
                    component={RouterLink}
                    href={paths.dashboard.packagingItem.edit(currentPackagingItem?._id ?? '')}
                  >
                    <Button variant="text">Edit</Button>
                  </Link>
                )}
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentPackagingItem?.itemName}
                  secondary={currentPackagingItem?.itemCode}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentPackagingItem?.unit.name}
                  secondary="Unit"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={`${currentPackagingItem?.reorderThreshold} ${currentPackagingItem?.unit.abbreviation}`}
                  secondary="Re-order threshold"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary={currentPackagingItem?.product?.productName ?? 'Not linked'}
                  secondary="Linked product"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Divider
                sx={{
                  my: 2.5,
                  typography: 'overline',
                  color: 'text.disabled',
                  '&:before, :after': {
                    borderTopStyle: 'dashed',
                  },
                }}
              />

              <Grid mb={1} xs={12} width="full">
                <Typography mt={2} variant="subtitle1">
                  Operational Information
                </Typography>
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary={`${currentPackagingItem?.quantity} ${currentPackagingItem?.unit.abbreviation}`}
                  secondary="Running Quantity"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary={
                    <Label variant="soft" color={stockLevel.color}>
                      {stockLevel.label}
                    </Label>
                  }
                  secondary="Status"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary={
                    <Label
                      variant="soft"
                      color={currentPackagingItem?.alertEnabled ? 'success' : 'warning'}
                    >
                      {currentPackagingItem?.alertEnabled ? 'Enabled' : 'Disabled'}
                    </Label>
                  }
                  secondary="Alerts"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              {currentPackagingItem?.suppliers && currentPackagingItem?.suppliers.length > 0 && (
                <>
                  <Grid mb={1} xs={12} width="full">
                    <Typography mt={2} variant="subtitle1">
                      Suppliers
                    </Typography>
                  </Grid>

                  <Grid xs={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {currentPackagingItem.suppliers.map((supplier) => (
                        <Link
                          key={supplier._id}
                          component={RouterLink}
                          href={paths.dashboard.suppliers.view(supplier._id)}
                        >
                          {supplier.supplierName}
                        </Link>
                      ))}
                    </Stack>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {tabValue === 'history' && (
            <Stack>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {currentPackagingItem?.itemName}
              </Typography>

              <TableContainer
                sx={{ position: 'relative', overflow: 'hidden', height: '450px', mt: 2 }}
              >
                <Scrollbar>
                  <Table stickyHeader size="medium" sx={{ minWidth: 720 }}>
                    <TableHeadCustom headLabel={LEDGER_HEADERS} />

                    <TableBody>
                      {history.status === 'loading' ? (
                        [...Array(10)].map((i, index) => <TableSkeleton key={index} />)
                      ) : (
                        <>
                          {history.data?.ledger.map(
                            (entry: IPackagingItemHistoryEntry, index: number) => (
                              <TableRow hover key={`${entry.type}-${entry.reference}-${index}`}>
                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  <Label variant="soft" color={TYPE_COLOR[entry.type]}>
                                    {TYPE_LABEL[entry.type]}
                                  </Label>
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  {entry.reference && entry.recordId ? (
                                    <Link
                                      component={RouterLink}
                                      href={REFERENCE_PATH[entry.type](entry.recordId)}
                                    >
                                      {entry.reference}
                                    </Link>
                                  ) : (
                                    (entry.type === 'adjustment' && entry.adjustedBy
                                      ? `by ${entry.adjustedBy.firstName} ${entry.adjustedBy.lastName}`
                                      : entry.reference) ?? '-'
                                  )}
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  {fDateTime(entry.date)}
                                </TableCell>

                                <TableCell
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    textAlign: 'left',
                                    color: entry.quantity >= 0 ? 'success.main' : 'error.main',
                                  }}
                                >
                                  {entry.quantity >= 0 ? '+' : ''}
                                  {entry.quantity} {currentPackagingItem?.unit.abbreviation}
                                  {entry.type === 'adjustment' && (
                                    <Typography
                                      variant="caption"
                                      component="div"
                                      color="text.secondary"
                                    >
                                      {entry.previousQuantity} → {entry.newQuantity}
                                      {entry.reason ? ` · ${entry.reason}` : ''}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </>
                      )}

                      {history.status !== 'failure' && history.data?.ledger.length === 0 && (
                        <TableNoData message="No stock movements found" notFound />
                      )}

                      {history.status === 'failure' && (
                        <TableErrorData notFound error={history.error} />
                      )}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <Link component={RouterLink} href={paths.dashboard.packagingItem.root}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
